'use client';

import React from 'react';
import { Cookie } from 'lucide-react';
import PolicyLayout, {
  PolicySection,
  PolicySubSection,
  PolicyList,
  PolicyCallout,
  TocSection,
} from '@/components/PolicyLayout';

const sections: TocSection[] = [
  { id: 'what-are-cookies', title: 'What Are Cookies' },
  { id: 'auth-cookies', title: 'Authentication Cookies' },
  { id: 'analytics-cookies', title: 'Analytics Cookies' },
  { id: 'session-persistence', title: 'Session Persistence' },
  { id: 'browser-controls', title: 'Browser Controls' },
  { id: 'updates', title: 'Policy Updates' },
];

export default function CookiePolicyClient() {
  return (
    <PolicyLayout
      title="Cookie Policy"
      description="How JobCompanion uses cookies and similar technologies to keep you logged in, maintain your session, and understand platform usage."
      lastUpdated="May 18, 2026"
      icon={Cookie}
      iconBg="bg-warning/10"
      iconColor="text-warning"
      sections={sections}
      currentPath="/cookie-policy"
    >
      <PolicySection id="what-are-cookies" title="What Are Cookies">
        <p>
          Cookies are small text files stored on your device by your web browser when you visit a website. They allow websites to remember information about your visit, such as your login status and preferences.
        </p>
        <p>
          JobCompanion uses a minimal set of cookies — only those necessary to operate the platform and, optionally, to understand how users interact with it.
        </p>
        <PolicyCallout type="info">
          We do not use advertising cookies, behavioral tracking cookies, or third-party marketing cookies.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="auth-cookies" title="Authentication Cookies">
        <p>
          Authentication cookies are essential for the platform to function. They are set when you log in and are used to:
        </p>
        <PolicyList items={[
          'Verify your identity on each page request',
          'Maintain your logged-in state across browser sessions',
          'Protect your account from unauthorized access',
          'Enable secure API calls from your browser to our servers',
        ]} />
        <PolicySubSection title="Cookie Details">
          <PolicyList items={[
            'Name: sb-[project-ref]-auth-token (Supabase authentication token)',
            'Type: HttpOnly, Secure, SameSite=Lax',
            'Duration: Session-based (expires when you log out or session expires)',
            'Purpose: Authentication and session management',
            'Third-party: No — set by JobCompanion servers only',
          ]} />
        </PolicySubSection>
        <p>
          These cookies cannot be disabled without breaking your ability to log in and use the platform.
        </p>
      </PolicySection>

      <PolicySection id="analytics-cookies" title="Analytics Cookies">
        <p>
          If Google Analytics 4 (GA4) is configured on the platform, it may set analytics cookies to help us understand how users interact with JobCompanion. These cookies:
        </p>
        <PolicyList items={[
          'Collect anonymized data about page views, session duration, and feature usage',
          'Do not collect personally identifiable information',
          'Do not track you across other websites',
          'Are only active if a valid GA4 Measurement ID is configured',
        ]} />
        <PolicySubSection title="GA4 Cookie Details">
          <PolicyList items={[
            'Name: _ga, _ga_[measurement-id]',
            'Type: Persistent (expires after 2 years)',
            'Purpose: Distinguishing unique users for analytics',
            'Third-party: Google Analytics (processed by Google)',
          ]} />
        </PolicySubSection>
        <p>
          You can opt out of GA4 analytics using Google&apos;s opt-out browser add-on or by adjusting your browser&apos;s cookie settings.
        </p>
      </PolicySection>

      <PolicySection id="session-persistence" title="Session Persistence">
        <p>
          JobCompanion uses browser localStorage and sessionStorage in addition to cookies for:
        </p>
        <PolicyList items={[
          'Storing UI preferences (sidebar collapsed state, filter settings)',
          'Caching non-sensitive data to improve performance',
          'Maintaining form state during multi-step workflows',
        ]} />
        <p>
          This data is stored locally on your device and is not transmitted to our servers. It is cleared when you clear your browser data or log out.
        </p>
      </PolicySection>

      <PolicySection id="browser-controls" title="Browser Controls">
        <p>
          You can control cookies through your browser settings. Here is how to manage cookies in common browsers:
        </p>
        <PolicyList items={[
          'Chrome: Settings → Privacy and Security → Cookies and other site data',
          'Firefox: Settings → Privacy & Security → Cookies and Site Data',
          'Safari: Preferences → Privacy → Manage Website Data',
          'Edge: Settings → Cookies and site permissions → Cookies and site data',
        ]} />
        <PolicyCallout type="warning">
          Disabling authentication cookies will prevent you from logging in to JobCompanion. We recommend allowing cookies from our domain for full functionality.
        </PolicyCallout>
        <p>
          For questions about our cookie use, contact us at <a href="/contact-grievance" className="text-primary underline">our contact page</a>.
        </p>
      </PolicySection>

      <PolicySection id="updates" title="Policy Updates">
        <p>
          We may update this Cookie Policy as we add new features or change our analytics setup. The &quot;Last updated&quot; date at the top of this page reflects the most recent revision. Continued use of the platform after updates constitutes acceptance of the revised policy.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
